import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaTrash, FaPlus, FaEdit } from 'react-icons/fa';
import {
  TextField,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Button,
  Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import AddPositionGradesWizard from './AddPositionGradesWizard';
import { 
  Container, 
  Header, 
  HeaderLeft, 
  Title, 
  Content, 
  BackButton, 
  HeaderButton,
  TabContainer,
  TabButton,
  FormContainer,
  FormSection,
  SectionTitle,
  FormRow
} from '../../../EmployeesRepository/EmployeeForm/styles';
import LoadingSpinner from '../../../common/LoadingSpinner';
import { useAlert } from '../../../../contexts/AlertContext';
import { permissionsApi } from '../../../../services/api/permissionsApi';
import { COLORS } from '../../../../constants/radar';
import IconBrowser from './IconBrowser';
import { getRoleIcon } from '../utils/iconMap';
import Modal from '../../../common/Modal';
import ConfirmationModal from '../../../common/ConfirmationModal';
import { ColorGrid, ColorOption } from './styles';
import QualifyingConsultantsTab from './QualifyingConsultantsTab';
import { ACTIVE_STATUSES } from '../../../common/filters';

// Define predefined colors for the color picker
const PREDEFINED_COLORS = [
  '#2196F3', '#4CAF50', '#FF9800', '#F44336', 
  '#9C27B0', '#00BCD4', '#607D8B', '#795548',
  '#E91E63', '#8BC34A', '#009688', '#3F51B5',
  '#FF5722', '#CDDC39', '#FFC107', '#673AB7'
];

const TABS = {
  ROLE_DATA: 'ROLE_DATA',
  PERMISSIONS: 'PERMISSIONS',
  POSITIONS_GRADES: 'POSITIONS_GRADES',
  QUALIFYING_CONSULTANTS: 'QUALIFYING_CONSULTANTS'
};

const RoleDetails = () => {
  const navigate = useNavigate();
  const { roleId } = useParams();
  const { showAlert } = useAlert();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.ROLE_DATA);
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: '',
    isSystemRole: false,
    hierarchyLevel: 0,
    icon: 'Person',
    color: '#2196f3'
  });
  const [permissions, setPermissions] = useState({});
  const [iconBrowserOpen, setIconBrowserOpen] = useState(false);
  const [actions, setActions] = useState([]);
  const [actionsLoading, setActionsLoading] = useState(true);
  const [resources, setResources] = useState([]);
  const [resourcesLoading, setResourcesLoading] = useState(true);
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [selectedResourceAction, setSelectedResourceAction] = useState(null);
  const [selectedPermissionId, setSelectedPermissionId] = useState(null);
  const [modalMode, setModalMode] = useState('select'); // 'select' or 'remove'
  const [organizationalPositions, setOrganizationalPositions] = useState([]);
  const [removePositionModal, setRemovePositionModal] = useState({
    open: false,
    position: null
  });
  const [addPositionGradesWizardOpen, setAddPositionGradesWizardOpen] = useState(false);
  const [managePositionGradesModal, setManagePositionGradesModal] = useState({
    open: false,
    positionMapping: null
  });

  // Qualifying consultants state
  const [qualifyingConsultants, setQualifyingConsultants] = useState([]);
  const [qualifyingConsultantsLoading, setQualifyingConsultantsLoading] = useState(false);
  const [qualifyingConsultantsLoadingMore, setQualifyingConsultantsLoadingMore] = useState(false);
  const [qualifyingConsultantsPageInfo, setQualifyingConsultantsPageInfo] = useState(null);
  const [qualifyingClusterFilter, setQualifyingClusterFilter] = useState('');
  const [qualifyingActiveStatuses, setQualifyingActiveStatuses] = useState([ACTIVE_STATUSES.ACTIVE]);

  useEffect(() => {
    loadActions();
    loadResources();
    if (roleId && roleId !== 'new') {
      loadRoleData();
    } else if (roleId === 'new') {
      // Set default values for new role
      setFormData({
        name: '',
        displayName: '',
        description: '',
        isSystemRole: false,
        hierarchyLevel: 0,
        icon: 'Person',
        color: '#2196f3'
      });
      setPermissions({});
    }
  }, [roleId, navigate, showAlert]);

  // Load qualifying consultants when tab is selected or filters change
  useEffect(() => {
    if (activeTab !== TABS.QUALIFYING_CONSULTANTS || !roleId || roleId === 'new') {
      return;
    }

    let isCancelled = false;

    const loadData = async () => {
      try {
        setQualifyingConsultantsLoading(true);
        setQualifyingConsultants([]);

        // Determine isActive filter value based on selected statuses
        let isActive;
        const hasActive = qualifyingActiveStatuses.includes(ACTIVE_STATUSES.ACTIVE);
        const hasInactive = qualifyingActiveStatuses.includes(ACTIVE_STATUSES.INACTIVE);

        if (hasActive && !hasInactive) {
          isActive = true;
        } else if (hasInactive && !hasActive) {
          isActive = false;
        }
        // If both or none selected, isActive remains undefined (no filter)

        const response = await permissionsApi.getQualifyingEmployees(roleId, {
          limit: 20,
          clusterId: qualifyingClusterFilter || undefined,
          isActive
        });

        if (!isCancelled) {
          setQualifyingConsultants(response.items || []);
          setQualifyingConsultantsPageInfo(response.pageInfo || null);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Error loading qualifying individuals:', error);
          showAlert(error.message || 'Failed to load qualifying individuals', 'error');
        }
      } finally {
        if (!isCancelled) {
          setQualifyingConsultantsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isCancelled = true;
    };
  }, [activeTab, roleId, qualifyingClusterFilter, qualifyingActiveStatuses, showAlert]);

  const loadMoreQualifyingConsultants = useCallback(() => {
    if (!qualifyingConsultantsPageInfo?.hasNextPage || qualifyingConsultantsLoadingMore) {
      return;
    }

    const loadMore = async () => {
      try {
        setQualifyingConsultantsLoadingMore(true);

        // Determine isActive filter value based on selected statuses
        let isActive;
        const hasActive = qualifyingActiveStatuses.includes(ACTIVE_STATUSES.ACTIVE);
        const hasInactive = qualifyingActiveStatuses.includes(ACTIVE_STATUSES.INACTIVE);

        if (hasActive && !hasInactive) {
          isActive = true;
        } else if (hasInactive && !hasActive) {
          isActive = false;
        }

        const response = await permissionsApi.getQualifyingEmployees(roleId, {
          limit: 20,
          cursor: qualifyingConsultantsPageInfo.endCursor,
          clusterId: qualifyingClusterFilter || undefined,
          isActive
        });

        if (response.items && response.items.length > 0) {
          setQualifyingConsultants(prev => [...prev, ...response.items]);
        }
        setQualifyingConsultantsPageInfo(response.pageInfo || null);
      } catch (error) {
        console.error('Error loading more qualifying individuals:', error);
        showAlert(error.message || 'Failed to load more qualifying individuals', 'error');
      } finally {
        setQualifyingConsultantsLoadingMore(false);
      }
    };

    loadMore();
  }, [roleId, qualifyingClusterFilter, qualifyingActiveStatuses, qualifyingConsultantsPageInfo, qualifyingConsultantsLoadingMore, showAlert]);

  const handleQualifyingActiveStatusToggle = useCallback((status) => {
    setQualifyingActiveStatuses(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      }
      return [...prev, status];
    });
  }, []);

  const loadActions = async () => {
    try {
      setActionsLoading(true);
      const actionsData = await permissionsApi.getAllActions({ published: true });
      setActions(actionsData); // Store full action objects
    } catch (error) {
      console.error('Error loading actions:', error);
      showAlert(error.message || 'Failed to load actions', 'error');
      setActions([]);
    } finally {
      setActionsLoading(false);
    }
  };

  const loadResources = async () => {
    try {
      setResourcesLoading(true);
      const resourcesData = await permissionsApi.getAllResources({ published: true });
      setResources(resourcesData); // Store full resource objects, not just names
    } catch (error) {
      console.error('Error loading resources:', error);
      showAlert(error.message || 'Failed to load resources', 'error');
      setResources([]);
    } finally {
      setResourcesLoading(false);
    }
  };

  const loadRoleData = async () => {
    try {
      setIsLoading(true);
      const roleData = await permissionsApi.getRoleById(roleId);
      setFormData({
        name: roleData.name,
        displayName: roleData.displayName || '',
        description: roleData.description || '',
        isSystemRole: roleData.isSystemRole || false,
        hierarchyLevel: roleData.hierarchyLevel || 0,
        icon: roleData.icon || 'Person',
        color: roleData.color || '#2196f3'
      });
      setPermissions(roleData.rolePermissions || []);

      // Load position mappings with grades
      const positionMappings = roleData.rolePositionMappings || [];

      // Fetch grades for each position mapping that supports grades
      const positionsWithGrades = await Promise.all(
        positionMappings.map(async (mapping) => {
          if (mapping.position?.requiresGrade) {
            try {
              const grades = await permissionsApi.getRolePositionMappingGrades(mapping.id);
              return { ...mapping, grades };
            } catch (error) {
              console.error(`Error fetching grades for mapping ${mapping.id}:`, error);
              return { ...mapping, grades: [] };
            }
          }
          return { ...mapping, grades: [] };
        })
      );

      setOrganizationalPositions(positionsWithGrades);
    } catch (error) {
      console.error('Error loading role:', error);
      showAlert(error.message || `Role not found`, 'error');
      navigate('/permissions/roles');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    try {
      // Validate required fields
      if (!formData.name.trim()) {
        showAlert('Role name is required', 'error');
        return;
      }
      if (!formData.displayName.trim()) {
        showAlert('Display name is required', 'error');
        return;
      }

      const roleData = {
        name: formData.name.trim(),
        displayName: formData.displayName.trim(),
        description: formData.description.trim(),
        isSystemRole: formData.isSystemRole,
        hierarchyLevel: formData.hierarchyLevel,
        icon: formData.icon,
        color: formData.color
      };
      
      if (roleId === 'new') {
        const newRole = await permissionsApi.createRole(roleData);
        showAlert('Role created successfully', 'success');
        // Navigate to the edit page for the new role
        navigate(`/permissions/roles/${newRole.id}`);
      } else {
        await permissionsApi.updateRole(roleId, roleData);
        showAlert('Role updated successfully', 'success');
      }
    } catch (error) {
      console.error('Error saving role:', error);
      showAlert(error.message || `Failed to ${roleId === 'new' ? 'create' : 'update'} role`, 'error');
    }
  };

  const handleIconSelect = (iconName) => {
    setFormData(prev => ({
      ...prev,
      icon: iconName
    }));
  };

  const handleColorSelect = (color) => {
    setFormData(prev => ({
      ...prev,
      color: color
    }));
  };

  const hasPermission = (resource, action) => {
    return permissions.some(permission => 
      permission.permission.resource?.id === resource.id &&
      permission.permission.action?.id === action.id
    );
  };

  const getResourceActionPermissions = (resource, action) => {
    // Get all permissions for this resource-action combination
    if (!resource.permissions || !Array.isArray(resource.permissions)) {
      return [];
    }
    return resource.permissions.filter(permission => 
      permission.action?.id === action.id 
    );
  };

  const getPermissionCount = (resource, action) => {
    return getResourceActionPermissions(resource, action).length;
  };

  const resourceHasAction = (resource, action) => {
    return getResourceActionPermissions(resource, action).length > 0;
  };

  const handleMultiplePermissionsClick = (resource, action, mode = 'select') => {
    setSelectedResourceAction({ resource, action });
    setModalMode(mode);
    setPermissionsModalOpen(true);
  };

  const handlePermissionsModalClose = () => {
    setPermissionsModalOpen(false);
    setSelectedResourceAction(null);
    setSelectedPermissionId(null);
    setModalMode('select');
  };

  const handlePermissionSelect = (permissionId) => {
    setSelectedPermissionId(permissionId);
  };

  // Simplified modal permissions - only for selection since removal is direct
  const modalAvailablePermissions = useMemo(() => {
    if (!selectedResourceAction || modalMode !== 'select') return [];
    
    // Get all permissions for this resource-action combination
    const resourcePermissions = getResourceActionPermissions(selectedResourceAction.resource, selectedResourceAction.action);
    
    // Get permission IDs that are already assigned to this role
    const assignedPermissionIds = permissions
      .filter(rolePermission => 
        rolePermission.permission.resource?.id === selectedResourceAction.resource.id &&
        rolePermission.permission.action?.id === selectedResourceAction.action.id
      )
      .map(rolePermission => rolePermission.permission.id);
    
    // Return only permissions that are NOT already assigned to this role
    return resourcePermissions.filter(permission => 
      !assignedPermissionIds.includes(permission.id)
    );
  }, [selectedResourceAction, modalMode, resources, permissions]);

  const handlePermissionSubmit = async () => {
    if (selectedPermissionId && selectedResourceAction && roleId && roleId !== 'new') {
      try {
        // Assign the selected permission to the role
        const newRolePermission = await permissionsApi.assignPermissionToRole({
          roleId: roleId,
          permissionId: selectedPermissionId
        });
        
        // Find the selected permission details from available permissions
        const selectedPermission = modalAvailablePermissions.find(p => p.id === selectedPermissionId);
        
        if (selectedPermission) {
          // Update local state - add the new permission
          // Ensure the permission structure matches what hasPermission expects
          const newPermissionEntry = {
            id: newRolePermission.id || `temp-${Date.now()}`, // Use API response ID or temporary ID
            roleId: roleId,
            permissionId: selectedPermissionId,
            permission: {
              id: selectedPermissionId,
              resource: {
                id: selectedResourceAction.resource.id,
                name: selectedResourceAction.resource.name
              },
              action: {
                id: selectedResourceAction.action.id,
                name: selectedResourceAction.action.name
              },
              conditions: selectedPermission.conditions || {},
              reason: selectedPermission.reason || null
            }
          };
          
          setPermissions(prevPermissions => [...prevPermissions, newPermissionEntry]);
        }
        
        showAlert('Permission assigned to role successfully', 'success');
        
        handlePermissionsModalClose();
        
        // No need to reload - state is updated locally
      } catch (error) {
        console.error('Error assigning permission:', error);
        showAlert(error.message || 'Failed to assign permission', 'error');
      }
    }
  };

  const handleOpenAddPositionWizard = () => {
    setAddPositionGradesWizardOpen(true);
  };

  const handleCloseAddPositionWizard = () => {
    setAddPositionGradesWizardOpen(false);
  };

  const handleAddPositionSuccess = async () => {
    await loadRoleData();
  };

  const handleOpenManageGrades = (positionMapping) => {
    setManagePositionGradesModal({
      open: true,
      positionMapping
    });
  };

  const handleCloseManageGrades = () => {
    setManagePositionGradesModal({
      open: false,
      positionMapping: null
    });
  };

  const handleManageGradesSuccess = async () => {
    await loadRoleData();
  };

  const handleRemovePosition = (position) => {
    setRemovePositionModal({
      open: true,
      position: position
    });
  };

  const confirmRemovePosition = async () => {
    if (!roleId || roleId === 'new' || !removePositionModal.position) return;

    const positionId = removePositionModal.position.position?.id || removePositionModal.position.positionId;

    try {
      await permissionsApi.removePositionFromRole({
        roleId: roleId,
        positionId: positionId
      });

      // Reload role data to get updated positions
      await loadRoleData();
      showAlert('Position removed from role successfully', 'success');
      setRemovePositionModal({ open: false, position: null });
    } catch (error) {
      console.error('Error removing position:', error);
      showAlert(error.message || 'Failed to remove position', 'error');
      setRemovePositionModal({ open: false, position: null });
    }
  };

  const togglePermission = async (resource, action) => {
    if (!roleId || roleId === 'new') return;
    
    try {
      // Check if the role currently has this permission
      const roleHasPermission = hasPermission(resource, action);
      
      if (roleHasPermission) {
        // Role has permission - remove it directly (no modal needed since only one can be assigned)
        const rolePermissionForAction = permissions.find(rolePermission => 
          rolePermission.permission.resource?.id === resource.id &&
          rolePermission.permission.action?.id === action.id
        );
        
        if (rolePermissionForAction) {
          await permissionsApi.removePermissionFromRole({
            roleId: roleId,
            permissionId: rolePermissionForAction.permission.id
          });
          
          // Update local state - remove the permission
          setPermissions(prevPermissions => 
            prevPermissions.filter(rp => rp.id !== rolePermissionForAction.id)
          );
          
          showAlert('Permission removed from role', 'success');
        }
      } else {
        // Role doesn't have permission - assign one
        // Check available permissions for this resource-action combination
        const availablePermissions = getResourceActionPermissions(resource, action);
        
        if (availablePermissions.length === 1) {
          // Single available permission - assign it directly
          const newRolePermission = await permissionsApi.assignPermissionToRole({
            roleId: roleId,
            permissionId: availablePermissions[0].id
          });
          
          // Update local state - add the new permission
          // Ensure the permission structure matches what hasPermission expects
          const newPermissionEntry = {
            id: newRolePermission.id || `temp-${Date.now()}`, // Use API response ID or temporary ID
            roleId: roleId,
            permissionId: availablePermissions[0].id,
            permission: {
              id: availablePermissions[0].id,
              resource: {
                id: resource.id,
                name: resource.name
              },
              action: {
                id: action.id,
                name: action.name
              },
              conditions: availablePermissions[0].conditions || {},
              reason: availablePermissions[0].reason || null
            }
          };
          
          setPermissions(prevPermissions => [...prevPermissions, newPermissionEntry]);
          
          showAlert('Permission assigned to role', 'success');
        } else if (availablePermissions.length > 1) {
          // Multiple available permissions - show modal to select which one to assign
          handleMultiplePermissionsClick(resource, action, 'select');
          return;
        } else {
          // No available permissions for this resource-action combination
          showAlert(`No permissions available for ${action.name} on ${resource.name}. Please create a permission first.`, 'warning');
          return;
        }
      }
      
      // No need to reload - state is updated locally
    } catch (error) {
      console.error('Error toggling permission:', error);
      showAlert(error.message || 'Failed to toggle permission', 'error');
    }
  };


  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/permissions/roles')}>
            <FaArrowLeft /> Back to Roles
          </BackButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar 
              sx={{ 
                bgcolor: formData.color, 
                width: 40, 
                height: 40 
              }}
            >
              {getRoleIcon(formData.icon)}
            </Avatar>
            <Title>{roleId === 'new' ? 'Create New Role' : formData.displayName}</Title>
          </Box>
        </HeaderLeft>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {activeTab === TABS.ROLE_DATA && (
            <HeaderButton onClick={handleSave} sx={{ 
              backgroundColor: COLORS.PRIMARY,
              color: 'white',
              '&:hover': {
                backgroundColor: '#1d4ed8',
              }
            }}>
              {roleId === 'new' ? 'Create Role' : 'Save Changes'}
            </HeaderButton>
          )}
        </Box>
      </Header>

      <Content>
        <TabContainer>
          <TabButton
            data-active={activeTab === TABS.ROLE_DATA}
            onClick={() => setActiveTab(TABS.ROLE_DATA)}
          >
            Role Data
          </TabButton>
          {roleId !== 'new' && (
            <TabButton
              data-active={activeTab === TABS.PERMISSIONS}
              onClick={() => setActiveTab(TABS.PERMISSIONS)}
            >
              Permissions
            </TabButton>
          )}
          {roleId !== 'new' && !formData.isSystemRole && (
            <TabButton
              data-active={activeTab === TABS.POSITIONS_GRADES}
              onClick={() => setActiveTab(TABS.POSITIONS_GRADES)}
            >
              Positions & Grades
            </TabButton>
          )}
          {roleId !== 'new' && (
            <TabButton
              data-active={activeTab === TABS.QUALIFYING_CONSULTANTS}
              onClick={() => setActiveTab(TABS.QUALIFYING_CONSULTANTS)}
            >
              Qualifying Individuals
            </TabButton>
          )}
        </TabContainer>

        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
            {activeTab === TABS.ROLE_DATA && (
              <FormContainer>
                <FormSection>
                  <SectionTitle>Basic Information</SectionTitle>
                  <FormRow>
                    <TextField
                      label="Name"
                      name="name"
                      required
                      fullWidth
                      value={formData.name}
                      onChange={handleInputChange}
                      disabled={roleId !== 'new'} // Only disable for existing roles
                      helperText="Internal identifier used in system logic"
                    />
                    <TextField
                      label="Display Name"
                      name="displayName"
                      required
                      fullWidth
                      value={formData.displayName}
                      onChange={handleInputChange}
                      helperText="User-facing label shown in UI"
                    />
                  </FormRow>
                  <FormRow>
                    <TextField
                      label="Description"
                      name="description"
                      fullWidth
                      multiline
                      rows={3}
                      value={formData.description}
                      onChange={handleInputChange}
                      helperText="Describe the purpose or scope of this role"
                    />
                  </FormRow>
                  <FormRow>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>Icon</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: formData.color, 
                            width: 48, 
                            height: 48 
                          }}
                        >
                          {getRoleIcon(formData.icon)}
                        </Avatar>
                        <Button 
                          variant="outlined" 
                          onClick={() => setIconBrowserOpen(true)}
                          sx={{ minWidth: '150px' }}
                        >
                          Choose Icon
                        </Button>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Selected: {formData.icon}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>Color</Typography>
                      <TextField
                        label="Color Code"
                        name="color"
                        required
                        fullWidth
                        variant="outlined"
                        placeholder="Enter color hex code (e.g. #2196F3)"
                        value={formData.color}
                        onChange={handleInputChange}
                        autoComplete="off"
                        sx={{ mb: 2 }}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <Box 
                                sx={{ 
                                  width: 24, 
                                  height: 24, 
                                  backgroundColor: formData.color,
                                  borderRadius: '50%',
                                  border: '1px solid rgba(0,0,0,0.1)',
                                  ml: 1,
                                  display: 'inline-block',
                                  flexShrink: 0,
                                  minWidth: '24px'
                                }} 
                              />
                            ),
                          }
                        }}
                      />
                      <Typography variant="body2" sx={{ mb: 1 }}>Or select from predefined colors:</Typography>
                      <ColorGrid>
                        {PREDEFINED_COLORS.map((color) => (
                          <ColorOption
                            key={color}
                            color={color}
                            selected={formData.color === color}
                            onClick={() => handleColorSelect(color)}
                            aria-label={`Select color ${color}`}
                          />
                        ))}
                      </ColorGrid>
                    </Box>
                  </FormRow>
                  
                  <FormRow>
                    <TextField
                      label="Hierarchy Level"
                      name="hierarchyLevel"
                      type="number"
                      fullWidth
                      value={formData.hierarchyLevel}
                      onChange={handleInputChange}
                      slotProps={{ 
                        htmlInput: { min: 0, max: 100 }
                      }}
                      helperText="Higher values = broader access (0-100)"
                    />
                  </FormRow>
                  <FormRow>
                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="isSystemRole"
                            checked={formData.isSystemRole}
                            onChange={handleInputChange}
                            disabled={roleId !== 'new' && formData.isSystemRole} // Can't change system role flag for existing system roles
                          />
                        }
                        label="System Role"
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                        System roles are built-in and protected from deletion
                      </Typography>
                    </Box>
                  </FormRow>
                </FormSection>
              </FormContainer>
            )}

            {activeTab === TABS.PERMISSIONS && (
              <FormContainer>
                <FormSection>
                  <SectionTitle>Role Permissions</SectionTitle>

                  {(actionsLoading || resourcesLoading) ? (
                    <LoadingSpinner message="Loading permissions data..." />
                  ) : (
                    <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell align="center" sx={{ width: '60px' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              #
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              Resource
                            </Typography>
                          </TableCell>
                          {actions.map((action) => (
                            <TableCell key={action.id} align="center">
                              <Chip
                                label={action.name}
                                size="small"
                                variant="outlined"
                                sx={{
                                  borderColor: action.color,
                                  color: action.color,
                                  backgroundColor: 'transparent',
                                  fontWeight: 500,
                                  fontSize: '12px',
                                  textTransform: 'capitalize',
                                  minWidth: '60px'
                                }}
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {resources.map((resource, index) => (
                          <TableRow key={resource.id}>
                            <TableCell align="center">
                              <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                                {index + 1}
                              </Typography>
                            </TableCell>
                            <TableCell component="th" scope="row">
                              <Typography variant="body2" fontWeight="medium">
                                {resource.name}
                              </Typography>
                            </TableCell>
                            {actions.map((action) => (
                              <TableCell key={action.id} align="center">
                                {resourceHasAction(resource, action) ? (
                                  (() => {
                                    const permissionCount = getPermissionCount(resource, action);
                                    const isActive = hasPermission(resource, action);

                                    if (!isActive && permissionCount > 1) {
                                      // Role doesn't have permission but multiple are available - show group icon for selection
                                      return (
                                        <Tooltip title={`${permissionCount} permissions available for ${action.name.toUpperCase()} on ${resource.name}. Click to select one.`}>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleMultiplePermissionsClick(resource, action, 'select')}
                                            color="default"
                                          >
                                            <GroupWorkIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      );
                                    } else {
                                      // Role has permission (show check) or only one permission available (show check/cancel)
                                      return (
                                        <Tooltip title={`Toggle ${action.name.toUpperCase()} permission for ${resource.name}`}>
                                          <IconButton
                                            size="small"
                                            onClick={() => togglePermission(resource, action)}
                                            color={isActive ? 'success' : 'default'}
                                          >
                                            {isActive ? (
                                              <CheckCircleIcon fontSize="small" />
                                            ) : (
                                              <CancelIcon fontSize="small" />
                                            )}
                                          </IconButton>
                                        </Tooltip>
                                      );
                                    }
                                  })()
                                ) : (
                                  // No icon if resource doesn't have this action
                                  <Box sx={{ width: 20, height: 20 }} />
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  )}
                </FormSection>
              </FormContainer>
            )}

            {activeTab === TABS.POSITIONS_GRADES && (
              <FormContainer>
                <FormSection>
                  <SectionTitle>Attach Position & Grades to Role</SectionTitle>
                  {/* Add Position Section */}
                  <Box sx={{ mb: 4 }}>
                    <HeaderButton
                      onClick={handleOpenAddPositionWizard}
                      sx={{
                        minWidth: 'auto',
                        padding: '8px 16px',
                        backgroundColor: '#2563eb',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#1d4ed8',
                        }
                      }}
                    >
                      <FaPlus style={{ marginRight: '8px' }} /> Add Position & Grades
                    </HeaderButton>
                  </Box>

                  {/* Assigned Positions List */}
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Assigned Positions & Grades ({organizationalPositions.length})
                    </Typography>

                    {organizationalPositions.length === 0 ? (
                      <Box sx={{
                        p: 4,
                        textAlign: 'center',
                        backgroundColor: '#f9fafb',
                        borderRadius: 2,
                        border: '1px dashed #d1d5db'
                      }}>
                        <Typography variant="body1" color="text.secondary">
                          No positions assigned to this role yet.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Click "Add Position & Grades" above to assign positions to this role.
                        </Typography>
                      </Box>
                    ) : (
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                              <TableCell sx={{ width: '60px', textAlign: 'center' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  #
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  Position
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  Grades
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ width: '120px', textAlign: 'center' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  Actions
                                </Typography>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {organizationalPositions.map((mapping, index) => (
                              <TableRow key={mapping.id}>
                                <TableCell align="center">
                                  <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                                    {index + 1}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="medium">
                                    {mapping.position?.label || mapping.label}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {mapping.position?.requiresGrade ? (
                                    mapping.grades && mapping.grades.length > 0 ? (
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {[...mapping.grades]
                                          .sort((a, b) => (a.grade?.order || 0) - (b.grade?.order || 0))
                                          .map(gradeMapping => (
                                          <Chip
                                            key={gradeMapping.id}
                                            label={gradeMapping.grade?.label || 'Unknown'}
                                            size="small"
                                            sx={{
                                              backgroundColor: COLORS.PRIMARY,
                                              color: 'white',
                                              fontSize: '0.75rem'
                                            }}
                                          />
                                        ))}
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">
                                        No grades assigned
                                      </Typography>
                                    )
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                      Not applicable
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="center">
                                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                                    {mapping.position?.requiresGrade ? (
                                      <Tooltip title="Manage grades">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleOpenManageGrades(mapping)}
                                          color="primary"
                                        >
                                          <FaEdit />
                                        </IconButton>
                                      </Tooltip>
                                    ) : (
                                      <Box sx={{ width: 28 }} />
                                    )}
                                    <Tooltip title="Remove position from role">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleRemovePosition(mapping)}
                                        color="error"
                                      >
                                        <FaTrash />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                </FormSection>
              </FormContainer>
            )}

            {activeTab === TABS.QUALIFYING_CONSULTANTS && (
              <QualifyingConsultantsTab
                  consultants={qualifyingConsultants}
                  isLoading={qualifyingConsultantsLoading}
                  isLoadingMore={qualifyingConsultantsLoadingMore}
                  pageInfo={qualifyingConsultantsPageInfo}
                  loadMoreData={loadMoreQualifyingConsultants}
                  clusterFilter={qualifyingClusterFilter}
                  onClusterFilterChange={setQualifyingClusterFilter}
                  activeStatuses={qualifyingActiveStatuses}
                  onActiveStatusToggle={handleQualifyingActiveStatusToggle}
              />
            )}
          </Box>
        )}
      </Content>

      {/* Icon Browser Dialog */}
      <IconBrowser
        open={iconBrowserOpen}
        onClose={() => setIconBrowserOpen(false)}
        selectedIcon={formData.icon}
        onIconSelect={handleIconSelect}
      />

      {/* Multiple Permissions Modal */}
      <Modal
        isOpen={permissionsModalOpen}
        onClose={handlePermissionsModalClose}
        title={selectedResourceAction ? `Select Permission for ${selectedResourceAction.action.name} on ${selectedResourceAction.resource.name}` : 'Select Permission'}
        maxWidth="md"
        onSubmit={handlePermissionSubmit}
        submitText="Select Permission"
        submitDisabled={!selectedPermissionId}
      >
        <Box sx={{ p: 2 }}>
          {selectedResourceAction && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Click to select a permission:
              </Typography>
              {modalAvailablePermissions.map((permission, index) => {
                const isSelected = selectedPermissionId === permission.id;
                return (
                  <Box 
                    key={permission.id || index}
                    onClick={() => handlePermissionSelect(permission.id)}
                    sx={{ 
                      mb: 2, 
                      p: 2, 
                      border: isSelected ? '2px solid #2196f3' : '1px solid #e0e0e0', 
                      borderRadius: 1,
                      backgroundColor: isSelected ? '#e3f2fd' : '#f9f9f9',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: isSelected ? '#e3f2fd' : '#f0f0f0',
                        borderColor: isSelected ? '#2196f3' : '#ccc'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, flex: 1 }}>
                        Available Permission {index + 1}
                      </Typography>
                      {isSelected && (
                        <CheckCircleIcon color="primary" fontSize="small" />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Resource: {selectedResourceAction.resource.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Action: {permission.action?.name || selectedResourceAction.action.name}
                    </Typography>
                    {permission.conditions && Object.keys(permission.conditions).length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Conditions: {JSON.stringify(permission.conditions)}
                      </Typography>
                    )}
                    {permission.reason && (
                      <Typography variant="body2" color="text.secondary">
                        Reason: {permission.reason}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Modal>

      {/* Remove Position Confirmation Modal */}
      <ConfirmationModal
        isOpen={removePositionModal.open}
        onClose={() => setRemovePositionModal({ open: false, position: null })}
        onConfirm={confirmRemovePosition}
        title="Remove Position from Role"
        message={`Are you sure you want to remove "${removePositionModal.position?.position?.label || removePositionModal.position?.label}" from this role?`}
        actionButtonText="Remove Position"
        actionButtonColor="#dc2626"
      />

      {/* Add Position & Grades Wizard */}
      <AddPositionGradesWizard
        open={addPositionGradesWizardOpen}
        onClose={handleCloseAddPositionWizard}
        roleId={roleId}
        existingPositionIds={organizationalPositions.map(p => p.position?.id || p.positionId)}
        onSuccess={handleAddPositionSuccess}
      />

      {/* Manage Position Grades (Edit Mode) */}
      <AddPositionGradesWizard
        open={managePositionGradesModal.open}
        onClose={handleCloseManageGrades}
        roleId={roleId}
        onSuccess={handleManageGradesSuccess}
        editMode={true}
        positionMapping={managePositionGradesModal.positionMapping}
      />
    </Container>
  );
};

export default RoleDetails;